import { Controller } from "@hotwired/stimulus"

// Star-rating picker for the review form.
//
// The hidden input drives the actual form submission; the buttons are
// pure UI. Backend has matching presence + inclusion validation as a
// safety net, but per user intent the client MUST prevent submission
// without a rating — backend validation is "the very extreme back up"
// in their words.
//
// Markup expected:
//   <div data-controller="star-rating"
//        data-star-rating-value-value="0">
//     <input type="hidden" name="review[rating]"
//            data-star-rating-target="input">
//     <button type="button" data-action="click->star-rating#select"
//             data-star-rating-target="star" data-value="1">★</button>
//     ... 4 more
//     <span data-star-rating-target="label"></span>
//     <span data-star-rating-target="hint">Please pick a rating</span>
//   </div>
//
// To enable submit-blocking, the form's submit button(s) must carry
// `data-star-rating-target="submit"` and (optionally) the parent
// controller can declare a textual hint via `data-star-rating-target="hint"`.
export default class extends Controller {
  static targets = ["input", "star", "label", "submit", "hint"]
  static values = { value: { type: Number, default: 0 } }

  connect() {
    this.render(this.valueValue)
    this.syncSubmitState()
  }

  // Click handler — locks the rating in
  select(event) {
    const rating = parseInt(event.currentTarget.dataset.value, 10)
    this.valueValue = rating
    this.inputTarget.value = rating
    this.render(rating)
    this.syncSubmitState()
  }

  // Hover handler — previews without committing
  preview(event) {
    const rating = parseInt(event.currentTarget.dataset.value, 10)
    this.render(rating)
  }

  // Mouseleave restores the locked-in rating
  reset() {
    this.render(this.valueValue)
  }

  render(rating) {
    this.starTargets.forEach((star) => {
      const v = parseInt(star.dataset.value, 10)
      if (v <= rating) {
        star.classList.add("text-yellow-400")
        star.classList.remove("text-gray-300")
      } else {
        star.classList.add("text-gray-300")
        star.classList.remove("text-yellow-400")
      }
    })
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = rating > 0
        ? this.labelTarget.dataset[`label${rating}`] || `${rating} / 5`
        : ""
    }
  }

  // Submit button stays disabled (and visually muted) until a star is
  // picked. Belt: HTML disabled attribute. Suspenders: a click handler
  // on the form blocks submit if value is still 0 (covers form auto-fill
  // edge cases). The visible hint underneath tells users why.
  syncSubmitState() {
    const valid = this.valueValue >= 1 && this.valueValue <= 5
    this.submitTargets.forEach((btn) => {
      btn.disabled = !valid
      btn.classList.toggle("opacity-50", !valid)
      btn.classList.toggle("cursor-not-allowed", !valid)
    })
    if (this.hasHintTarget) {
      this.hintTarget.classList.toggle("hidden", valid)
    }
  }

  // Defensive: block form submission if somehow the button gets clicked
  // while we're not valid (autofill, keyboard navigation around disabled
  // state, etc.). Wire via data-action="submit->star-rating#guardSubmit"
  // on the form element.
  guardSubmit(event) {
    if (this.valueValue < 1 || this.valueValue > 5) {
      event.preventDefault()
      this.hintTarget?.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }
}
